/**
 * Pure derivations for the company overview. No React, no fetching, no clock.
 *
 * Everything here is a function of its arguments, so the rules that decide what a
 * number MEANS can be tested without rendering anything — and the component layer
 * stays free of arithmetic it could quietly get wrong.
 */
// The German month names and the window label already exist for the
// revenue-analytics page and the Performance tab (P2). Importing them keeps ONE
// month vocabulary; a local copy is how one screen ends up saying "Mär" while
// another says "Mrz".
import { periodRangeLabel } from "../../../pages/admin/revenueAnalytics/revenueAnalyticsFormat";

/**
 * A wire money value → a Number, or null when it is not a usable figure.
 *
 * THIS IS NOT DEFENSIVE PADDING. The two contracts the overview subtracts from one
 * another disagree about type on the wire: `revenue-analytics` money fields are
 * Python `Decimal` and serialise as STRINGS ("21493.00"), while the A2 cost
 * contract's are `float` and serialise as NUMBERS (12840.5). `expected - costs`
 * across that boundary is `NaN`, and `NaN` renders as "NaN" or, worse, gets
 * formatted into something that looks like a franc amount.
 *
 * null (not 0) for anything unusable: a missing figure is not a zero one, and the
 * difference decides whether the UI shows "—" or a confident CHF 0.
 */
export function toNumber(value) {
  if (value === null || value === undefined || value === "") return null;
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

/**
 * Expected contractual revenue for the selected SERVICE month.
 * `revenue-analytics.kpis.expected_total`.
 */
export function selectExpectedRevenue(revenueAnalytics) {
  return toNumber(revenueAnalytics?.kpis?.expected_total);
}

/**
 * Company-wide invoiced revenue for the selected INVOICE month (A1).
 *
 * Deliberately NOT `revenue-analytics.kpis.invoiced_total`: that figure is
 * attributed to the rent period proven by the draft ledger, which in production
 * covers a minority of mirrored invoices. This one selects on `issue_date` and
 * needs no mapping, so it is the company total rather than a subset of it.
 */
export function selectInvoiced(companyInvoiced) {
  return toNumber(companyInvoiced?.invoiced?.total_chf);
}

/**
 * Operating costs for the selected month (A2).
 *
 * Already includes `deposit_insurance_chf`. Callers must NOT add the premium
 * again — see `assertNoDoubleCountedPremium` for why that is worth a test.
 */
export function selectOperatingCosts(costBreakdown) {
  return toNumber(costBreakdown?.operating_costs?.total_chf);
}

/** Unclassified costs — reported beside the headline, never inside it. */
export function selectUnclassifiedCosts(costBreakdown) {
  return toNumber(costBreakdown?.unclassified?.total_monthly_equivalent_chf);
}

/** Cash actually received in the selected month. */
export function selectCashIn(revenueAnalytics) {
  return toNumber(revenueAnalytics?.cashflow?.received_in_selected_period);
}

/**
 * Open receivables — CURRENT STATE, not the selected month's historical balance.
 * The response carries `as_of_date`; the UI must label it accordingly.
 */
export function selectOpenReceivables(revenueAnalytics) {
  return toNumber(revenueAnalytics?.current_receivables?.open_current);
}

/**
 * Tenant cash deposits currently HELD, from `/deposits/cash-held`. Current state.
 *
 * Third-party money we hold for a tenant — a liability. Deliberately NOT
 * `landlord-placed.total_chf`, which is capital WE placed with an owner: the two are
 * opposite sides of the balance and share only the German word "Kaution". Reading one
 * field name where the other was meant is the confusion this narrow selector exists
 * to make impossible.
 *
 * Never revenue, never a cost, never part of the contribution margin.
 */
export function selectTenantCashDeposits(cashDepositsHeld) {
  return toNumber(cashDepositsHeld?.cash_deposits_total);
}

/**
 * Expected contribution margin: expected contractual revenue MINUS operating costs.
 *
 * `null` unless BOTH inputs are real. A missing cost figure would otherwise make the
 * contribution equal the full revenue — a number that looks like an excellent month
 * and is actually a failed request.
 *
 * Deliberately not the legacy `summary.actual_profit`: that subtracts a cost total
 * which includes unclassified rows, so it answers a different question with the same
 * shape. And never labelled "Gewinn" — this is contractual expectation minus
 * operating cost, not a result.
 */
export function selectExpectedContribution(expectedRevenue, operatingCosts) {
  if (expectedRevenue === null || operatingCosts === null) return null;
  return expectedRevenue - operatingCosts;
}

/**
 * Contribution as a percentage of expected revenue, or null.
 *
 * Zero expected revenue yields null rather than 0 %: with nothing expected there is
 * no margin to state, and 0 % would read as a real and very bad one.
 */
export function selectContributionMargin(expectedRevenue, contribution) {
  if (expectedRevenue === null || contribution === null) return null;
  if (expectedRevenue === 0) return null;
  return (contribution / expectedRevenue) * 100;
}

/**
 * The CURRENT portfolio snapshot, from `GET /api/admin/occupancy`. KPI **C.1**.
 *
 * A DIFFERENT AXIS FROM THE MANAGEMENT FIGURE (C.4, `selectManagementOccupancy`).
 * This one answers for a single day — the backend's own Zurich business date when no
 * `on_date` is sent — while C.4 answers for the whole selected period. The card
 * shows both, which is only honest because each is labelled with the axis it belongs
 * to, and a period change moves exactly one of them.
 *
 * `activeUnits` is `units.length` and nothing else. The endpoint gates the org-wide
 * aggregation on each unit's lease window (`get_org_occupancy` →
 * `units_in_portfolio`), so the array already IS the set of units live on that day;
 * counting `/units` in the browser would mean re-implementing that lifecycle rule
 * here and getting a different answer the day it changes.
 *
 * NO RATE WITHOUT A DENOMINATOR. The contract answers `occupancy_rate: 0.0` when the
 * portfolio has no rooms at all, because dividing by zero has to return something —
 * but "0 %" on screen is a statement that every room is empty, which is a very
 * different claim from "there are no rooms". So a zero `total_rooms` yields a null
 * rate and the card prints "—", while the room counts themselves stay visible.
 *
 * The rate is used exactly as received: `OccupancySummary.occupancy_rate` is already
 * a percentage (`occupied / total * 100`, rounded to one decimal by the service), so
 * there is deliberately no `* 100` anywhere on this path.
 */
export function selectCurrentOccupancy(occupancy) {
  const summary = occupancy?.summary;
  const totalRooms = toNumber(summary?.total_rooms);
  const hasRooms = totalRooms !== null && totalRooms > 0;
  return {
    rate: hasRooms ? toNumber(summary?.occupancy_rate) : null,
    totalRooms,
    occupiedRooms: toNumber(summary?.occupied_rooms),
    reservedRooms: toNumber(summary?.reserved_rooms),
    freeRooms: toNumber(summary?.free_rooms),
    activeUnits: Array.isArray(occupancy?.units) ? occupancy.units.length : null,
    hasRooms,
    // Whether there is a portfolio line to draw at all. A failed or absent response
    // has no counts to state, and a row of dashes states nothing worth the space.
    hasPortfolio: totalRooms !== null || Array.isArray(occupancy?.units),
  };
}

/**
 * The global period → the WINDOW the period-aware contracts are asked for (P3b).
 *
 * THIS IS THE LINE THE RANGE USED TO DIE ON. Until P3b the page collapsed
 * `{mode, from, to}` to `kpisPeriod.to` before the tab ever saw it, so a filter
 * reading "April – September 2026" asked five endpoints for September alone. There
 * is no collapse here: `from` reaches the wire.
 *
 * Returns the WIRE parameters, not an intermediate shape. Four of the five
 * period-aware contracts (`revenue-analytics`, `company-invoiced`,
 * `cost-breakdown`, `payments/recent`) take exactly `year`/`month` plus an optional
 * `to_year`/`to_month`, so the resolved window is spread straight into them and no
 * call site re-derives a calendar. C.4 is the one exception and names its lower
 * bound `from_*`; `roomDaysParams` below renames it in ONE place.
 *
 *   mode "month"  → the single month, with `to_*` equal to it. Sending the window
 *                   explicitly rather than omitting it keeps ONE code path, and the
 *                   backend's own answer for a one-month window is by construction
 *                   the single-month answer — it runs the same monthly computation.
 *   mode "custom" → the real window, both bounds.
 *   mode "all"    → null. "Gesamte Zeit" is unbounded and every one of these
 *                   contracts is bounded at 24 months; answering it with the
 *                   to-month would be the collapse this slice removes, and quietly
 *                   answering it with "the last 24 months" would put a period on
 *                   screen that the label does not name. See §19.
 *
 * `monthCount` and `isRange` are derived here ONLY to decide what to request and how
 * to label it while the response is still in flight. Every figure the user reads is
 * described by the period the SERVER reports back.
 */
export function selectOverviewWindow(period) {
  const mode = period?.mode;
  if (mode === "all") return null;

  const to = period?.to;
  const from = period?.from;

  // A "custom" period whose lower bound is unusable is REFUSED, not completed from
  // the upper one. Completing it is precisely the collapse this function exists to
  // remove: the answer would look like the selection and be a different period.
  // A period that names only one bound is that one month, which is a real selection.
  const start = mode === "custom" ? from : to || from;
  const end = to || from;
  if (!start || start.year == null || start.month == null) return null;
  if (!end || end.year == null || end.month == null) return null;

  const year = Number(start.year);
  const month = Number(start.month);
  const toYear = Number(end.year);
  const toMonth = Number(end.month);
  // A reversed window is not a window. It cannot come from the picker, but a caller
  // that built one by hand must not have it silently swapped into something it did
  // not ask for.
  if (toYear * 12 + toMonth < year * 12 + month) return null;

  const monthCount = (toYear * 12 + toMonth) - (year * 12 + month) + 1;
  return {
    year,
    month,
    to_year: toYear,
    to_month: toMonth,
    monthCount,
    isRange: monthCount > 1,
  };
}

/**
 * The same window under C.4's parameter names.
 *
 * `/occupancy/room-days` calls its lower bound `from_year`/`from_month` because it
 * has no single-month legacy contract to stay compatible with — it was born taking a
 * window. The rename lives here so no component holds two vocabularies for one
 * period.
 */
export function roomDaysParams(window) {
  if (!window) return null;
  return {
    from_year: window.year,
    from_month: window.month,
    to_year: window.to_year,
    to_month: window.to_month,
  };
}

/**
 * A resolved window as a stable key — used to discard a previous period's response.
 *
 * Names the WHOLE window, not just a month: "Apr–Sep" and "Sep alone" would
 * otherwise produce the same key, which was harmless while the tab genuinely
 * answered for one month and is a stale-response bug the moment it answers for a
 * range. Same rule as `performanceRequestKey` (P2).
 */
export function overviewWindowKey(window) {
  if (!window) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return (
    `${window.year}-${pad(window.month)}` +
    `..${window.to_year}-${pad(window.to_month)}`
  );
}

/**
 * A window as a heading: "September 2026", "April – September 2026",
 * "Oktober 2025 – März 2026".
 *
 * Delegates to the shared `periodRangeLabel`, which already collapses an equal pair
 * to a single month label — so a single-month selection reads exactly as it always
 * did and no caller needs an `isRange` branch to get that.
 */
export function windowLabel(window) {
  if (!window) return "";
  return periodRangeLabel(
    { year: window.year, month: window.month },
    { year: window.to_year, month: window.to_month }
  );
}

/**
 * Management occupancy for the selected period — KPI **C.4**, `Σ occupied_room_days
 * / Σ available_room_days`.
 *
 * A DIFFERENT AGGREGATION FROM C.2, NOT A RENAMED ONE. `/occupancy/average` takes
 * the unweighted mean of the daily rates; this takes the ratio of the sums. The two
 * diverge whenever the months are unequal in length or the portfolio changed size
 * inside the window — February at 100 % and August at 0 % is 47.5 % here and 50 %
 * there — and both are right for the question they were asked. C.2 keeps its meaning
 * and its endpoint; it simply is not the figure a management period view shows.
 *
 * NO ARITHMETIC. `occupancy_pct` is already a percentage and the counters are
 * already counters; the ratio is computed once, server-side, over the whole window.
 * Re-deriving it here from the two sums would be a second implementation of C.4.
 *
 * `occupancy_pct` is null when the window has no available room-days at all — a
 * portfolio with nothing to let has no rate, and 0 % would claim every room stood
 * empty. `toNumber` carries that null through rather than turning it into a zero.
 */
export function selectManagementOccupancy(roomDays) {
  return {
    rate: toNumber(roomDays?.occupancy_pct),
    occupiedRoomDays: toNumber(roomDays?.occupied_room_days),
    availableRoomDays: toNumber(roomDays?.available_room_days),
  };
}

/** A trend point (or a single-month period) as a stable key. */
export function periodKey(period) {
  return period ? `${period.year}-${String(period.month).padStart(2, "0")}` : "";
}

/**
 * The count behind the warnings badge: real rows from both sources, never a literal.
 */
export function selectWarningCount(alerts, criticalUnits) {
  const a = Array.isArray(alerts?.alerts) ? alerts.alerts.length : 0;
  const c = Array.isArray(criticalUnits?.critical_units)
    ? criticalUnits.critical_units.length
    : 0;
  return a + c;
}

/**
 * The label for one payment row.
 *
 * Falls back contact name → document number → provider invoice id. Never invents a
 * tenant: a payment on a general invoice legitimately has no tenancy, and calling it
 * "Unbekannter Mieter" would report a data gap where none exists.
 */
export function paymentLabel(payment) {
  const contact = String(payment?.contact_name || "").trim();
  if (contact) return contact;
  const doc = String(payment?.document_nr || "").trim();
  if (doc) return doc;
  const bexioId = payment?.bexio_invoice_id;
  return bexioId != null ? `Bexio-Rechnung ${bexioId}` : "Zahlungseingang";
}

/** The secondary line of a payment row: the document number, when it adds anything. */
export function paymentSubLabel(payment) {
  const doc = String(payment?.document_nr || "").trim();
  if (!doc) return null;
  return doc === paymentLabel(payment) ? null : doc;
}
